import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import apiService, {
  parseSearchPaymentDetailsResponse,
  SearchPaymentDetailsRequest,
} from "../../services/api";
import { getHubId } from "../../utils/cookieUtils";
import useHubs from "../../hooks/useHubs";
import useRoleBasedFiltering from "../../hooks/useRoleBasedFiltering";
import Pagination from "../../components/ui/Pagination";
import SortableTable, {
  SortableColumn,
} from "../../components/common/SortableTable";
import { usePagination } from "../../hooks/usePagination";
import SearchableSelectInput from "../../components/forms/SearchableSelectInput";
import ActionIcon from "../../../src/assets/icons/ActionIcon.svg";
import { useAppSelector } from "../../hooks/useAuth";
import CustomLoading from "../../components/common/CustomLoading";
import { formatMobileNumber } from "../../utils/formatters";
import { DATE_FORMATS } from "../../constants";
import { parsePaymentSummaryForPlanVehicle } from "../../utils/paymentSummaryParse";
import { hasPaymentRefundableAmount } from "../../utils/paymentRefundEligibility";
import { useNotification } from "../../contexts/NotificationContext";
import { useHeaderToolbarAfterExport } from "../../contexts/HeaderToolbarContext";

dayjs.extend(utc);

function toNumber(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function isBookingRefundEligible(summary: string): boolean {
  return String(summary ?? "")
    .toLowerCase()
    .includes("booking");
}

function isTruthyRefundAddedFlag(v: unknown): boolean {
  return (
    v === true ||
    v === 1 ||
    (typeof v === "string" && v.trim().toLowerCase() === "true")
  );
}

interface PaymentData {
  id: string;
  bookingId: string;
  userName: string;
  mobileNo: string;
  hub: string;
  hubmasterid?: string | number;
  dateTime: string;
  amount: number;
  discountedAmountDisplay: string;
  method: string;
  summaryOfPayments: string;
  plan: string;
  vehicle: string;
  bookingmasterid: number;
  bookingplanid: number;
  refundableamount: number | null;
  /** True when API marks this payment as already having a refund (same as Payment Details hide rule). */
  refundAlreadyAdded: boolean;
  /** Column anchor for bulk-select checkbox (SortableTable); not used as data */
  tableSelect?: undefined;
}

function isBulkRefundRowSelectable(row: PaymentData): boolean {
  return (
    isBookingRefundEligible(row.summaryOfPayments) &&
    !row.refundAlreadyAdded &&
    row.refundableamount !== null
  );
}

function formatPaymentDiscountedAmountForTable(
  p: Record<string, unknown>,
): string {
  const raw = p.discountedamount ?? p.discountedAmount;
  const n = Number.parseFloat(String(raw ?? 0));

  return `₹${(Number.isFinite(n) ? n : 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const transformApiDataToPaymentData = (apiData: any[]): PaymentData[] =>
  apiData.map((p: any, index: number) => {
    const { plan, vehicle } = parsePaymentSummaryForPlanVehicle(
      p.summeryofpayments || "",
    );
    const summaryText = String(p.summeryofpayments ?? "");
    const pr = p as Record<string, unknown>;
    return {
      id: p.paymentid?.toString() || index.toString(),
      bookingId: p.bookingid || "",
      userName: p.username || "",
      mobileNo: formatMobileNumber(p.mobileno) || "",
      hub: p.hubname || "",
      hubmasterid: p.hubmasterid || p.hubid,
      dateTime: DATE_FORMATS.formatDateTime(p.enteredon),
      amount: p.amount || 0,
      discountedAmountDisplay: formatPaymentDiscountedAmountForTable(
        p as Record<string, unknown>,
      ),
      method:
        p.paymentmethod === 1
          ? "Card"
          : p.paymentmethod === 2
            ? "UPI"
            : p.paymentmethod === 3
              ? "Wallet"
              : "NetBanking",
      summaryOfPayments: summaryText,
      plan: plan,
      vehicle: vehicle,
      bookingmasterid: toNumber(
        p.bookingmasterid ?? p.bookingmasterId ?? p.bookingid,
      ),
      bookingplanid: toNumber(
        p.bookingplanid ?? p.bookingPlanId ?? p.planid ?? p.planId,
      ),
      refundAlreadyAdded:
        isTruthyRefundAddedFlag(pr.isrefundadded) ||
        isTruthyRefundAddedFlag(pr.isRefundAdded) ||
        isTruthyRefundAddedFlag(pr.IsRefundAdded),
      hasRefundableAmount: hasPaymentRefundableAmount(pr),
      refundableamount: (() => {
        const raw = pr.refundableamount ?? pr.refundableAmount;
        if (raw == null || raw === "") return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      })(),
    };
  });

/**
 * Maps Plan/Vehicle UI filters to `summeryofpayments` search text.
 * Rows may use legacy `Payment for {plan} - {vehicle}` or new `Booking payment for {vehicle} - {plan}`;
 * both plan and vehicle tokens together usually match either via substring search.
 */
const buildSummeryOfPaymentsFilter = (
  plan: string,
  vehicle: string,
): string => {
  const p = plan.trim();
  const v = vehicle.trim();
  if (!p && !v) return "";
  if (p && v) return `${p} ${v}`;
  if (p) return `Payment for ${p}`;
  return v;
};

const PaymentManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const initialFilters = location.state?.filters || {};
  const initialPage = location.state?.page || 0;

  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>(
    () => {
      const from = initialFilters.dateRange?.[0];
      const to = initialFilters.dateRange?.[1];
      if (from && to) {
        return [dayjs(from), dayjs(to)];
      }
      return [dayjs().startOf("month"), dayjs()];
    },
  );
  const [month, setMonth] = useState(initialFilters.month || "");
  const [hub, setHub] = useState(initialFilters.hub || "");
  const [bookingId, setBookingId] = useState(initialFilters.bookingId || "");
  const [userPhone, setUserPhone] = useState(initialFilters.userPhone || "");
  const [usernameFilter, setUsernameFilter] = useState(
    initialFilters.usernameFilter || "",
  );
  const [transactionId, setTransactionId] = useState(
    initialFilters.transactionId || "",
  );
  const [plan, setPlan] = useState(initialFilters.plan || "");
  const [vehicle, setVehicle] = useState(initialFilters.vehicle || "");
  const [payments, setPayments] = useState<PaymentData[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const dateRangeRef = useRef(dateRange);
  dateRangeRef.current = dateRange;

  const { hubOptions, loading: hubsLoading } = useHubs(setError);
  const { filterByRole } = useRoleBasedFiltering<PaymentData>();
  const { user } = useAppSelector((state) => state.auth);
  const isSuperAdmin = user?.roleid === 1;
  const { showNotification } = useNotification();
  const { setAfterExportSlot } = useHeaderToolbarAfterExport();

  const [selectedPaymentIds, setSelectedPaymentIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [bulkRefundDialogOpen, setBulkRefundDialogOpen] = useState(false);
  const [bulkRefundRemark, setBulkRefundRemark] = useState("");
  const [bulkRefundSubmitting, setBulkRefundSubmitting] = useState(false);
  const [bulkRefundError, setBulkRefundError] = useState<string | null>(null);

  const resolveHubMasterIdForApi = useCallback((): number => {
    if (!isSuperAdmin) {
      const hid = getHubId();
      return hid != null && Number.isFinite(Number(hid)) ? Number(hid) : 0;
    }
    if (hub) {
      const found = hubOptions.find((h) => h.hubname === hub);
      return found ? Number(found.hubmasterid) : 0;
    }
    return 0;
  }, [isSuperAdmin, hub, hubOptions]);

  const pagination = usePagination({
    totalItems: totalCount,
    initialPage: initialPage,
    initialRowsPerPage: 10,
  });

  const fetchPaymentDetails = useCallback(
    async (pageOverride?: number, rowsOverride?: number) => {
      try {
        setLoading(true);
        setError(null);

        const effectivePage =
          pageOverride !== undefined ? pageOverride : pagination.page;
        const effectiveRows =
          rowsOverride !== undefined ? rowsOverride : pagination.rowsPerPage;

        const [rangeFrom, rangeTo] = dateRangeRef.current;
        const fromIso = rangeFrom
          ? rangeFrom.startOf("day").utcOffset(0, true).toISOString()
          : dayjs().startOf("month").utcOffset(0, true).toISOString();
        const toIso = rangeTo
          ? rangeTo.endOf("day").utcOffset(0, true).toISOString()
          : dayjs().endOf("day").utcOffset(0, true).toISOString();

        const requestBody: SearchPaymentDetailsRequest = {
          bookingid: bookingId.trim(),
          phone: userPhone.trim(),
          transactionid: transactionId.trim(),
          fromdate: fromIso,
          todate: toIso,
          month: parseInt(String(month), 10) || 0,
          paymentid: 0,
          username: usernameFilter.trim(),
          mobileno: userPhone.trim(),
          amount: 0,
          paymentmethod: 0,
          summeryofpayments: buildSummeryOfPaymentsFilter(plan, vehicle),
          enteredon: "",
          hubmasterid: resolveHubMasterIdForApi(),
          pageNumber: effectivePage + 1,
          pageSize: effectiveRows,
        };

        const response = await apiService.searchPaymentDetails(requestBody);
        const { items, total } = parseSearchPaymentDetailsResponse(response);
        const transformedData = transformApiDataToPaymentData(items as any[]);
        const roleFilteredData = filterByRole(transformedData, "hubmasterid");

        setTotalCount(total);
        setPayments(roleFilteredData);
        setSelectedPaymentIds(new Set());
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Failed to load payments";
        setError(message);
        setTotalCount(0);
        setPayments([]);
      } finally {
        setLoading(false);
      }
    },
    [
      bookingId,
      userPhone,
      usernameFilter,
      transactionId,
      month,
      plan,
      vehicle,
      resolveHubMasterIdForApi,
      pagination.page,
      pagination.rowsPerPage,
      filterByRole,
    ],
  );

  const handleDateRangeChange = (newDateRange: [any, any]) => {
    const start = newDateRange[0]
      ? newDateRange[0].format
        ? newDateRange[0]
        : dayjs(newDateRange[0])
      : null;
    const end = newDateRange[1]
      ? newDateRange[1].format
        ? newDateRange[1]
        : dayjs(newDateRange[1])
      : null;
    setDateRange([start, end]);
  };

  const handleApplyDateRange = () => {
    setDatePickerOpen(false);
    if (dateRange[0] && dateRange[1]) {
      pagination.goToPage(0);
      void fetchPaymentDetails(0);
    }
  };

  const hubNameOptions = useMemo(
    () => (hubsLoading ? [] : hubOptions.map((h) => h.hubname)),
    [hubsLoading, hubOptions],
  );

  useEffect(() => {
    try {
      const exportFilters: SearchPaymentDetailsRequest = {
        bookingid: bookingId.trim(),
        phone: userPhone.trim(),
        transactionid: transactionId.trim(),
        fromdate: dateRange[0]
          ? dateRange[0].startOf("day").utcOffset(0, true).toISOString()
          : dayjs().startOf("month").utcOffset(0, true).toISOString(),
        todate: dateRange[1]
          ? dateRange[1].endOf("day").utcOffset(0, true).toISOString()
          : dayjs().endOf("day").utcOffset(0, true).toISOString(),
        month: parseInt(String(month), 10) || 0,
        paymentid: 0,
        username: usernameFilter.trim(),
        mobileno: userPhone.trim(),
        amount: 0,
        paymentmethod: 0,
        summeryofpayments: buildSummeryOfPaymentsFilter(plan, vehicle),
        enteredon: "",
        hubmasterid: resolveHubMasterIdForApi(),
        pageNumber: 1,
        pageSize: 10000,
      };
      localStorage.setItem(
        "paymentExportFilters",
        JSON.stringify(exportFilters),
      );
    } catch {
      /* ignore */
    }
  }, [
    bookingId,
    userPhone,
    usernameFilter,
    transactionId,
    dateRange,
    month,
    plan,
    vehicle,
    hub,
    hubOptions,
    resolveHubMasterIdForApi,
  ]);

  const confirmDeletePayment = async () => {
    if (!paymentToDelete) return;
    try {
      setDeleting(true);
      await apiService.deletePayment(paymentToDelete);
      setDeleteDialogOpen(false);
      void fetchPaymentDetails();
    } catch (err: any) {
      setError(err.message || "Failed to delete payment");
    } finally {
      setDeleting(false);
    }
  };

  const handleViewPaymentDetails = useCallback(
    (paymentId: string) => {
      const filters = {
        bookingId,
        userPhone,
        usernameFilter,
        transactionId,
        dateRange: [
          dateRange[0]?.toISOString() || null,
          dateRange[1]?.toISOString() || null,
        ],
        month,
        plan,
        vehicle,
        hub,
      };
      navigate(`/payment-management/details/${paymentId}`, {
        state: { filters, page: pagination.page },
      });
    },
    [
      bookingId,
      userPhone,
      usernameFilter,
      transactionId,
      dateRange,
      month,
      plan,
      vehicle,
      hub,
      pagination.page,
      navigate,
    ],
  );

  const eligiblePaymentsOnPage = useMemo(
    () => payments.filter((p) => isBulkRefundRowSelectable(p)),
    [payments],
  );

  const selectAllCheckboxState = useMemo(() => {
    const eligible = eligiblePaymentsOnPage;
    if (eligible.length === 0) {
      return { checked: false, indeterminate: false };
    }
    const selectedCount = eligible.filter((p) =>
      selectedPaymentIds.has(p.id),
    ).length;
    return {
      checked: selectedCount === eligible.length && selectedCount > 0,
      indeterminate: selectedCount > 0 && selectedCount < eligible.length,
    };
  }, [eligiblePaymentsOnPage, selectedPaymentIds]);

  const toggleSelectPayment = useCallback(
    (paymentId: string, checked: boolean) => {
      setSelectedPaymentIds((prev) => {
        const next = new Set(prev);
        if (checked) next.add(paymentId);
        else next.delete(paymentId);
        return next;
      });
    },
    [],
  );

  const toggleSelectAllEligible = useCallback(() => {
    setSelectedPaymentIds((prev) => {
      const next = new Set(prev);
      const eligible = payments.filter((p) => isBulkRefundRowSelectable(p));
      const allSelected =
        eligible.length > 0 && eligible.every((p) => prev.has(p.id));
      if (allSelected) {
        for (const p of eligible) {
          next.delete(p.id);
        }
      } else {
        for (const p of eligible) {
          next.add(p.id);
        }
      }
      return next;
    });
  }, [payments]);

  const issueCouponHeaderBlue = "rgba(22, 67, 135, 1)";
  const issueCouponSubmitYellow = "#FFEB3B";
  const issueCouponPrimaryButtonSx = {
    backgroundColor: issueCouponSubmitYellow,
    color: issueCouponHeaderBlue,
    fontWeight: 700,
    textTransform: "none" as const,
    px: 2.5,
    py: 1,
    minHeight: 40,
    borderRadius: 1,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#FDD835",
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: "rgba(255, 235, 59, 0.5)",
      color: "rgba(22, 67, 135, 0.6)",
    },
  };

  const closeBulkRefundDialog = () => {
    if (bulkRefundSubmitting) return;
    setBulkRefundDialogOpen(false);
    setBulkRefundError(null);
  };

  const handleBulkRefundSubmit = async () => {
    const remark = bulkRefundRemark.trim();
    if (remark === "") {
      setBulkRefundError("Please enter a remark.");
      return;
    }
    const rows = payments.filter(
      (p) => selectedPaymentIds.has(p.id) && isBulkRefundRowSelectable(p),
    );
    if (rows.length === 0) {
      setBulkRefundError("No valid booking payments selected.");
      return;
    }
    const payloads = rows.map((row) => {
      const bookingmasterid = row.bookingmasterid;
      const bookingplanid = row.bookingplanid;
      const amount = toNumber(row.amount);
      return {
        bookingmasterid,
        bookingplanid,
        amount,
        refunddescription: remark,
        refundType: "0",
        isaddedbyweb: true,
      };
    });
    const invalid = payloads.find(
      (p) => p.bookingmasterid <= 0 || p.amount <= 0,
    );
    if (invalid) {
      setBulkRefundError(
        "One or more selected rows are missing booking ID or amount.",
      );
      return;
    }
    setBulkRefundSubmitting(true);
    setBulkRefundError(null);
    try {
      const res = (await apiService.addRefund(payloads)) as {
        isSuccess?: boolean;
        success?: boolean;
        message?: string;
        Message?: string;
      };
      if (
        res &&
        typeof res === "object" &&
        (res.isSuccess === false || res.success === false)
      ) {
        const msg =
          (typeof res.message === "string" && res.message.trim() !== ""
            ? res.message.trim()
            : null) ??
          (typeof res.Message === "string" && res.Message.trim() !== ""
            ? res.Message.trim()
            : null) ??
          "Refund request failed.";
        throw new Error(msg);
      }
      const msg =
        (typeof res?.message === "string" && res.message.trim() !== ""
          ? res.message.trim()
          : null) ??
        (typeof res?.Message === "string" && res.Message.trim() !== ""
          ? res.Message.trim()
          : null) ??
        "Refund request submitted successfully.";
      setBulkRefundDialogOpen(false);
      setSelectedPaymentIds(new Set());
      showNotification(msg, "success");
      void fetchPaymentDetails();
    } catch (e: unknown) {
      const msg =
        e instanceof Error && e.message.trim() !== ""
          ? e.message
          : "Failed to process refund.";
      showNotification(msg, "error");
    } finally {
      setBulkRefundSubmitting(false);
    }
  };

  const columns: SortableColumn<PaymentData>[] = useMemo(
    () => [
      {
        id: "tableSelect",
        label: "",
        align: "center",
        sortable: false,
        headerRender: () => (
          <Checkbox
            size="small"
            checked={selectAllCheckboxState.checked}
            indeterminate={selectAllCheckboxState.indeterminate}
            disabled={eligiblePaymentsOnPage.length === 0}
            onChange={() => toggleSelectAllEligible()}
          />
        ),
        render: (row) => {
          const eligible = isBulkRefundRowSelectable(row);
          return (
            <Checkbox
              size="small"
              disabled={!eligible}
              checked={eligible && selectedPaymentIds.has(row.id)}
              onChange={(e) => {
                e.stopPropagation();
                if (!eligible) return;
                toggleSelectPayment(row.id, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
              inputProps={{
                "aria-label": eligible
                  ? `Select payment ${row.id}`
                  : row.refundAlreadyAdded
                    ? "Refund already added for this payment"
                    : row.refundableamount === null
                      ? "No refundable amount for this payment"
                      : "Not eligible for refund on this row",
              }}
            />
          );
        },
      },
      { id: "bookingId", label: "Booking ID" },
      { id: "userName", label: "User Name" },
      { id: "mobileNo", label: "Mobile No." },
      { id: "hub", label: "Hub" },
      { id: "dateTime", label: "Date & Time" },
      {
        id: "amount",
        label: "Total Amount (₹)",
        align: "center",
        render: (row) => `₹${row.amount}`,
      },
      {
        id: "discountedAmountDisplay",
        label: "Discounted Amount (₹)",
        align: "center",
        render: (row) => row.discountedAmountDisplay || "",
      },
      { id: "plan", label: "Plan", align: "center" },
      { id: "vehicle", label: "Vehicle", align: "center" },
      {
        id: "actions" as keyof PaymentData,
        label: "Action",
        align: "center",
        sortable: false,
        render: (row) => (
          <Button
            variant="text"
            sx={{ p: 0, minWidth: "auto" }}
            onClick={() => handleViewPaymentDetails(row.id)}
          >
            <img src={ActionIcon} alt="Action" width={30} height={30} />
          </Button>
        ),
      },
    ],
    [
      selectAllCheckboxState.checked,
      selectAllCheckboxState.indeterminate,
      eligiblePaymentsOnPage.length,
      toggleSelectAllEligible,
      selectedPaymentIds,
      toggleSelectPayment,
      handleViewPaymentDetails,
    ],
  );

  useEffect(() => {
    const state = location.state as
      | { refresh?: boolean; refreshHubs?: boolean }
      | undefined;
    if (state?.refresh || state?.refreshHubs) {
      void fetchPaymentDetails();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname, fetchPaymentDetails]);

  useEffect(() => {
    void fetchPaymentDetails();
  }, [fetchPaymentDetails]);

  useEffect(() => {
    setAfterExportSlot(
      <Button
        variant="contained"
        disabled={selectedPaymentIds.size === 0 || loading}
        onClick={() => {
          if (selectedPaymentIds.size === 0) return;
          setBulkRefundRemark("");
          setBulkRefundError(null);
          setBulkRefundDialogOpen(true);
        }}
        sx={{
          backgroundColor: "rgba(22, 67, 135, 1)",
          textTransform: "none",
          fontWeight: 600,
          minWidth: 140,
          "&:hover": { backgroundColor: "rgba(18, 57, 116, 1)" },
        }}
      >
        Process Refund
        {selectedPaymentIds.size > 0 ? ` (${selectedPaymentIds.size})` : ""}
      </Button>,
    );
    return () => setAfterExportSlot(null);
  }, [
    loading,
    selectedPaymentIds.size,
    setAfterExportSlot,
    setBulkRefundDialogOpen,
    setBulkRefundError,
    setBulkRefundRemark,
  ]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container fluid className="px-0">
        <Paper className="p-3 mb-3 border-0 shadow-none rounded-0">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={3}>
              <TextField
                label="Date Range"
                value={
                  dateRange[0] && dateRange[1]
                    ? `${dateRange[0].format(
                        "DD/MM/YYYY",
                      )} - ${dateRange[1].format("DD/MM/YYYY")}`
                    : ""
                }
                onClick={() => setDatePickerOpen(true)}
                size="small"
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <Button
                      size="small"
                      onClick={() => setDatePickerOpen(true)}
                    >
                      📅
                    </Button>
                  ),
                }}
              />
              <Dialog
                open={datePickerOpen}
                onClose={() => setDatePickerOpen(false)}
                fullWidth
              >
                <DialogTitle>Select Date Range</DialogTitle>
                <DialogContent>
                  <Box display="flex" gap={2} justifyContent="center" mt={2}>
                    <DatePicker
                      label="From"
                      value={dateRange[0]}
                      onChange={(v) => handleDateRangeChange([v, dateRange[1]])}
                      slotProps={{ textField: { size: "small" } }}
                      format="DD/MM/YYYY"
                    />
                    <DatePicker
                      label="To"
                      value={dateRange[1]}
                      onChange={(v) => handleDateRangeChange([dateRange[0], v])}
                      slotProps={{ textField: { size: "small" } }}
                      format="DD/MM/YYYY"
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setDatePickerOpen(false)}>
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={handleApplyDateRange}>
                    Apply
                  </Button>
                </DialogActions>
              </Dialog>
            </Col>
            {isSuperAdmin && (
              <Col xs={12} md={3}>
                <SearchableSelectInput
                  field="hub"
                  formData={{ hub }}
                  onChange={(_, value) => {
                    setHub(value);
                    pagination.goToPage(0);
                    navigate(location.pathname, {
                      replace: true,
                      state: {
                        ...((location.state as object) || {}),
                        filters: {
                          ...((location.state as { filters?: object })
                            ?.filters || {}),
                          hub: value,
                        },
                        page: 0,
                      },
                    });
                  }}
                  options={hubNameOptions}
                  label="Hub"
                  placeholder="Search and select hub"
                  disabled={hubsLoading}
                />
              </Col>
            )}

            <Col xs={12} md={3}>
              <TextField
                label="Booking ID"
                size="small"
                fullWidth
                value={bookingId}
                onChange={(e) => {
                  const newBookingId = e.target.value;
                  setBookingId(newBookingId);
                  pagination.goToPage(0);
                  navigate(location.pathname, {
                    replace: true,
                    state: {
                      ...((location.state as object) || {}),
                      filters: {
                        ...((location.state as any)?.filters || {}),
                        bookingId: newBookingId,
                      },
                      page: 0,
                    },
                  });
                }}
              />
            </Col>

            <Col xs={12} md={3}>
              <TextField
                label="User name"
                size="small"
                fullWidth
                value={usernameFilter}
                onChange={(e) => {
                  const v = e.target.value;
                  setUsernameFilter(v);
                  pagination.goToPage(0);
                  navigate(location.pathname, {
                    replace: true,
                    state: {
                      ...((location.state as object) || {}),
                      filters: {
                        ...((location.state as any)?.filters || {}),
                        usernameFilter: v,
                      },
                      page: 0,
                    },
                  });
                }}
              />
            </Col>

            <Col xs={12} md={3}>
              <TextField
                label="User Phone"
                size="small"
                fullWidth
                value={userPhone}
                onChange={(e) => {
                  const newUserPhone = e.target.value;
                  setUserPhone(newUserPhone);
                  pagination.goToPage(0);
                  navigate(location.pathname, {
                    replace: true,
                    state: {
                      ...((location.state as object) || {}),
                      filters: {
                        ...((location.state as any)?.filters || {}),
                        userPhone: newUserPhone,
                      },
                      page: 0,
                    },
                  });
                }}
              />
            </Col>

            <Col xs={12} md={3}>
              <TextField
                label="Transaction ID"
                size="small"
                fullWidth
                value={transactionId}
                onChange={(e) => {
                  const newTransactionId = e.target.value;
                  setTransactionId(newTransactionId);
                  pagination.goToPage(0);
                  navigate(location.pathname, {
                    replace: true,
                    state: {
                      ...((location.state as object) || {}),
                      filters: {
                        ...((location.state as any)?.filters || {}),
                        transactionId: newTransactionId,
                      },
                      page: 0,
                    },
                  });
                }}
              />
            </Col>
            <Col xs={12} md={3}>
              <TextField
                label="Plan"
                size="small"
                fullWidth
                placeholder="Matches payment summary (plan)"
                value={plan}
                onChange={(e) => {
                  const v = e.target.value;
                  setPlan(v);
                  pagination.goToPage(0);
                  navigate(location.pathname, {
                    replace: true,
                    state: {
                      ...((location.state as object) || {}),
                      filters: {
                        ...((location.state as any)?.filters || {}),
                        plan: v,
                      },
                      page: 0,
                    },
                  });
                }}
              />
            </Col>
            <Col xs={12} md={3}>
              <TextField
                label="Vehicle"
                size="small"
                fullWidth
                placeholder="Matches payment summary (vehicle)"
                value={vehicle}
                onChange={(e) => {
                  const v = e.target.value;
                  setVehicle(v);
                  pagination.goToPage(0);
                  navigate(location.pathname, {
                    replace: true,
                    state: {
                      ...((location.state as object) || {}),
                      filters: {
                        ...((location.state as any)?.filters || {}),
                        vehicle: v,
                      },
                      page: 0,
                    },
                  });
                }}
              />
            </Col>
          </Row>
        </Paper>

        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            className="mb-3"
          >
            {error}
          </Alert>
        )}

        <Paper className="border-0 rounded-0 shadow-none">
          {loading ? (
            <CustomLoading message="Fetching Payment Table Data..." />
          ) : (
            <SortableTable
              data={payments}
              columns={columns}
              emptyMessage="No payments found"
            />
          )}
        </Paper>

        <Pagination
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          totalItems={totalCount}
          totalPages={pagination.totalPages}
          startItem={pagination.startItem}
          endItem={pagination.endItem}
          rowsPerPageOptions={[5, 10, 25, 50]}
          showRowsPerPage
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          onGoToNextPage={pagination.goToNextPage}
          onGoToPreviousPage={pagination.goToPreviousPage}
        />

        <Dialog
          open={bulkRefundDialogOpen}
          onClose={closeBulkRefundDialog}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 },
          }}
        >
          <DialogTitle
            component="div"
            sx={{
              m: 0,
              py: 1.75,
              px: 2,
              backgroundColor: issueCouponHeaderBlue,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontWeight: 700,
              fontSize: "1.125rem",
            }}
          >
            Process refund ({selectedPaymentIds.size})
          </DialogTitle>
          <DialogContent
            sx={{
              px: 2.5,
              pb: 2,
              pt: "15px !important",
              position: "relative",
            }}
          >
            {bulkRefundError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {bulkRefundError}
              </Alert>
            ) : null}
            <TextField
              fullWidth
              required
              label="Remark"
              value={bulkRefundRemark}
              onChange={(e) => setBulkRefundRemark(e.target.value)}
              multiline
              minRows={2}
            />
          </DialogContent>
          <DialogActions
            sx={{
              px: 2.5,
              pb: 2,
              pt: 1,
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              onClick={closeBulkRefundDialog}
              disabled={bulkRefundSubmitting}
              sx={{
                color: issueCouponHeaderBlue,
                fontWeight: 600,
                textTransform: "none",
                border: "1px solid rgba(22, 67, 135, 0.6)",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleBulkRefundSubmit()}
              disabled={bulkRefundSubmitting}
              sx={issueCouponPrimaryButtonSx}
            >
              {bulkRefundSubmitting ? "Submitting..." : "Submit Refund"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this payment record?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={confirmDeletePayment}
              disabled={deleting}
              sx={{
                backgroundColor: "rgba(197, 0, 0, 1)",
                "&:hover": { backgroundColor: "rgba(197, 0, 0, 0.8)" },
              }}
            >
              {deleting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Yes, Delete"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default PaymentManagementPage;
